import { LockOutlined, MailOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import { Alert, App, Button, Card, Divider, Form, Input, Space, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PhantomWalletButton from '../components/PhantomWalletButton';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import authService from '../services/authService';

const { Title, Text } = Typography;
const { useApp } = App;

const SHOW_EMAIL_PASSWORD_FORM = false;

const LoginPage: React.FC = () => {
  const { message } = useApp();
  const [loading, setLoading] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Check for session expiration message on mount
  useEffect(() => {
    const sessionExpired = sessionStorage.getItem('sessionExpired');
    if (sessionExpired === 'true') {
      setShowSessionExpired(true);
      sessionStorage.removeItem('sessionExpired');
      message.warning('Your session has expired. Please log in again.');
    }
  }, []);

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const token = await authService.loginWithEmail(values.email, values.password);
      login(token);
      message.success('Login successful!');
      navigate('/');
    } catch (error: any) {
      message.error(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleWalletLogin = async (token: string) => {
    login(token);
    message.success('Wallet authentication successful!');
    navigate('/');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
        position: 'relative',
      }}
    >
      <Button
        type="text"
        icon={theme === 'dark' ? <SunOutlined /> : <MoonOutlined />}
        onClick={toggleTheme}
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          zIndex: 10,
          color: 'rgba(255, 255, 255, 0.9)',
          fontSize: '18px',
        }}
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      />
      <Card
        style={{
          width: '100%',
          maxWidth: 450,
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <img
              src="/logo.png"
              alt="FlipScout logo"
              style={{ width: 64, height: 64, objectFit: 'contain', marginBottom: 8 }}
            />
            <Title level={2} style={{ marginBottom: 16, marginTop: -8 }}>
              Welcome to FlipScout
            </Title>
          </div>

          {showSessionExpired && (
            <Alert
              message="Session Expired"
              description="Your session has expired. Please log in again to continue."
              type="warning"
              showIcon
              closable
              onClose={() => setShowSessionExpired(false)}
            />
          )}

          <PhantomWalletButton onSuccess={handleWalletLogin} />

          {SHOW_EMAIL_PASSWORD_FORM && (
            <>
              <Divider>
                <Text type="secondary">OR</Text>
              </Divider>

              <Form
                name="login"
                onFinish={onFinish}
                layout="vertical"
                requiredMark={false}
                size="large"
              >
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: 'Please input your email!' },
                    { type: 'email', message: 'Please enter a valid email!' },
                  ]}
                  style={{ marginBottom: 12 }}
                >
                  <Input
                    prefix={
                      <MailOutlined
                        style={{
                          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.25)',
                        }}
                      />
                    }
                    placeholder="Email"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[{ required: true, message: 'Please input your password!' }]}
                  style={{ marginBottom: 12 }}
                >
                  <Input.Password
                    prefix={
                      <LockOutlined
                        style={{
                          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.25)',
                        }}
                      />
                    }
                    placeholder="Password"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 12 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                    style={{ height: 48 }}
                  >
                    Sign In
                  </Button>
                </Form.Item>
              </Form>
            </>
          )}

        </Space>
      </Card>
    </div>
  );
};

export default LoginPage;
